// MerchantPartnerFeeForm.js
import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Divider,
    Grid,
    InputAdornment,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import moment from "moment";

import { currencyFormat } from "helper/NumberFormat";
import { merchantFees, getMerchantDepositList, updateMerchantFees, merchantFeesHistory } from "services/merchantServices";
import MerchantDeposit from "./merchantDeposit";
import { TCLabel, TCTitleLabel } from "components/TopCourtComponents/Label/TopCourtLabel";
import TableComponent from "components/TopCourtComponents/Table/TableComponent";
import { toast } from "react-toastify";
import { getPackageSportCourtType } from "services/packageSportCourtTypeService";

/**
 * ✅ Implement these in your services:
 * 1) updateMerchantPartnerFees(merchantId, payload)
 * 2) getMerchantPartnerFeeHistory(merchantId)
 *
 * Payload shape in this UI:
 * {
 *   orderType: {
 *     courtBooking: { percentage, minimumCharge },
 *     addOn: { percentage, minimumCharge },
 *     class: { percentage, minimumCharge },
 *     courts: [{ courtId, title, percentage, minimumCharge }]
 *   },
 *   valuePack: [
 *     { valuePackCategoryId, title, enabled, percentage, minimumCharge }
 *   ]
 * }
 *
 * History rows shape:
 * [{ modifiedAt, adminName, title, oldValue, newValue }]
 */

const rowPaperSx = {
    px: 2,
    borderRadius: 0.75,
    backgroundColor: "#fff",
};

const sectionLabelSx = {
    width: 140,
    minWidth: 140,
    px: 2,
    py: 1,
    borderRadius: 0.5,
    fontWeight: 700,
};

function toNumberOrNull(v) {
    if (v === "" || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}

function FeeRowPercentMin({
    label,
    percentage,
    minimumCharge,
    onChange,
    disabled,
    indent = false,
}) {
    return (
        <Paper sx={rowPaperSx}>
            <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", md: "center" }}
            >
                <Box sx={{ minWidth: { md: 200 }, pl: indent ? 2 : 0 }}>
                    <TCLabel>
                        {indent ? `- ${label}` : label}
                    </TCLabel>
                </Box>

                <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    sx={{ flex: 1, justifyContent: { md: "flex-end" } }}
                >
                    <TextField
                        size="small"
                        value={percentage ?? ""}
                        onChange={(e) => onChange?.({ percentage: e.target.value.replace(/[^0-9]/g, "") })}
                        disabled={disabled}
                        placeholder="____"
                        inputProps={{ inputMode: "numeric" }}
                        InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                        sx={{ maxWidth: 140, minWidth: 90, flexShrink: 6 }}
                        slotProps={{ inputLabel: { shrink: true } }}
                    />

                    <Typography color="text.secondary" sx={{ fontWeight: 500 }}>
                        or
                    </Typography>

                    <TextField
                        size="small"
                        value={minimumCharge ?? ""}
                        onChange={(e) => onChange?.({ minimumCharge: e.target.value.replace(/[^0-9]/g, "") })}
                        disabled={disabled}
                        placeholder="____"
                        inputProps={{ inputMode: "numeric" }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">min</InputAdornment>,
                            endAdornment: <InputAdornment position="end">/ txn</InputAdornment>,
                        }}
                        sx={{ maxWidth: 260, minWidth: 190, flexShrink: 1 }}
                        slotProps={{ inputLabel: { shrink: true } }}
                    />
                </Stack>
            </Stack>
        </Paper>
    );
}

function FeeRowCheckboxPercentMin({
    label,
    enabled,
    percentage,
    minimumCharge,
    onChange,
    disabled,
}) {
    return (
        <Paper sx={rowPaperSx}>
            <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", md: "center" }}
            >
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: { md: 200 } }}>
                    {/* <Checkbox
                        checked={!!enabled}
                        onChange={(e) => onChange?.({ enabled: e.target.checked })}
                        disabled={disabled}
                    /> */}
                    <Typography>{label} :</Typography>
                </Stack>

                <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    sx={{ flex: 1, justifyContent: { md: "flex-end" } }}
                >
                    {/* <TextField
                        size="small"
                        value={percentage ?? ""}
                        onChange={(e) => onChange?.({ percentage: e.target.value })}
                        disabled={disabled || !enabled}
                        placeholder="____"
                        inputProps={{ inputMode: "numeric" }}
                        InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                        sx={{ width: 140 }}
                    /> */}

                    {/* <Typography color="text.secondary" sx={{ fontWeight: 500 }}>
                        or
                    </Typography> */}

                    <TextField
                        size="small"
                        value={minimumCharge ?? ""}
                        onChange={(e) => onChange?.({ minimumCharge: e.target.value })}
                        disabled={disabled || !enabled}
                        placeholder="____"
                        inputProps={{ inputMode: "numeric" }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">min</InputAdornment>,
                            endAdornment: <InputAdornment position="end">/ session</InputAdornment>,
                        }}
                        sx={{ maxWidth: 260, minWidth: 210 }}
                    />
                </Stack>
            </Stack>
        </Paper>
    );
}

const initFilter = {
    page: 0,
    limit: 20,
}

export default function MerchantPartnerFeeForm({ merchantId, placeId }) {
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [merchantDepositList, setMerchantDepositList] = useState([]);
    const [totalDataDepositList, setTotalDataDepositList] = useState(0);
    const [rows, setRows] = useState([]);
    const [feeFilter, setFeeFilter] = useState(initFilter);
    const [depositFilter, setDepositFilter] = useState(initFilter);

    // server snapshots (for diff)
    const [serverSnapshot, setServerSnapshot] = useState(null);

    // editable state (matches screenshot)
    const [form, setForm] = useState({
        orderType: {
            courtBooking: { percentage: "", minimumCharge: "" },
            addOn: { percentage: "", minimumCharge: "" },
            class: { percentage: "", minimumCharge: "" },
            courts: [], // [{ courtId, title, percentage, minimumCharge }]
        },
        valuePack: [], // [{ valuePackCategoryId, title, enabled, percentage, minimumCharge }]
    });

    const [historyRows, setHistoryRows] = useState([]);
    const [totalDataHistory, setDataTotalHistory] = useState(0)

    useEffect(() => {
        loadData()
    }, [])

    useEffect(() => {
        if (!merchantId && !placeId) return;
        loadAll();
        loadMerchantDepositRequest();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [merchantId]);

    useEffect(() => {
        loadMerchantFeeHistory();
    }, [feeFilter])

    const loadMerchantFeeHistory = async () => {
        let { data, error } = await merchantFeesHistory(
            merchantId,
            "",
            placeId,
            feeFilter.page,
            feeFilter.limit
        )
        if (data) {
            setHistoryRows(data.changes)
            setDataTotalHistory(data.totalData)
        }
    }

    useEffect(() => {
        loadMerchantDepositRequest();
    }, [depositFilter])

    const loadMerchantDepositRequest = async () => {
        let { data, error } = await getMerchantDepositList({
            merchantId: merchantId,
            isClosed: '',
            page: depositFilter.page,
            limit: depositFilter.limit
        });
        if (data) {
            setMerchantDepositList(data.merchantDeposit)
            setTotalDataDepositList(data.totalData)
        }
    }

    const loadAll = async () => {
        try {
            setIsLoading(true);
            console.log('here placeId', placeId)

            // we fetch ORDER + COURT + PACKAGE, then reshape to a single form
            const [orderRes, feeHistory] = await Promise.all([
                merchantFees(merchantId, "", placeId),
                merchantFeesHistory(merchantId, "", placeId, feeFilter.page, feeFilter.limit)
            ]);

            console.log('here historyRows', feeHistory?.data)
            setHistoryRows(feeHistory?.data.changes)
            setDataTotalHistory(feeHistory?.data.totalData)

            console.log('here orderRes?.data?.valuePackFee', orderRes?.data?.valuePackFee)

            const nextForm = {
                orderType: {
                    ...orderRes?.data?.fee
                    // courtBooking: getOrder("COURT_BOOKING"),
                    // addOn: getOrder("ADD_ON"),
                    // class: getOrder("CLASS"),
                    // courts,
                },
                valuePack: orderRes?.data?.valuePackFee || [],
            };
            const snapshot = JSON.parse(JSON.stringify(nextForm));
            setServerSnapshot(snapshot);
            setForm({
                orderType: {
                    courtBooking: {
                        percentage: nextForm.orderType.courtBooking.percentage,
                        minimumCharge: nextForm.orderType.courtBooking.minimumCharge,
                    },
                    addOn: {
                        percentage: nextForm.orderType.addOn.percentage,
                        minimumCharge: nextForm.orderType.addOn.minimumCharge,
                    },
                    class: {
                        percentage: nextForm.orderType.class.percentage,
                        minimumCharge: nextForm.orderType.class.minimumCharge,
                    },
                    // courts: nextForm.orderType.courts.map((c) => ({
                    //     courtId: c.courtId,
                    //     title: c.title,
                    //     percentage: c.percentage,
                    //     minimumCharge: c.minimumCharge,
                    // })),
                },
                valuePack: nextForm.valuePack.map((v) => ({
                    valuePackCategoryId: v.valuePackCategoryId,
                    title: v.title,
                    enabled: v.enabled,
                    percentage: v.percentage,
                    minimumCharge: v.minimumCharge,
                })),
            });

            await loadHistory();
        } finally {
            setIsLoading(false);
        }
    };

    const loadHistory = async () => {
        try {
            setIsLoadingHistory(true);
            // const { data } = await getMerchantPartnerFeeHistory(merchantId);
            // setHistoryRows(Array.isArray(data) ? data : []);
        } finally {
            setIsLoadingHistory(false);
        }
    };

    const setOrderPatch = (key, patch) => {
        setForm((prev) => ({
            ...prev,
            orderType: {
                ...prev.orderType,
                [key]: { ...prev.orderType[key], ...patch },
            },
        }));
    };

    const setCourtPatch = (idx, patch) => {
        setForm((prev) => {
            const next = [...prev.orderType.courts];
            next[idx] = { ...next[idx], ...patch };
            return { ...prev, orderType: { ...prev.orderType, courts: next } };
        });
    };

    const setValuePackPatch = (idx, patch) => {
        setForm((prev) => {
            const next = [...prev.valuePack];
            next[idx] = { ...next[idx], ...patch };
            return { ...prev, valuePack: next };
        });
    };

    const onReset = () => {
        if (!serverSnapshot) return;
        setForm({
            orderType: {
                courtBooking: {
                    percentage: serverSnapshot.orderType.courtBooking.percentage ?? "",
                    minimumCharge: serverSnapshot.orderType.courtBooking.minimumCharge ?? "",
                },
                addOn: {
                    percentage: serverSnapshot.orderType.addOn.percentage ?? "",
                    minimumCharge: serverSnapshot.orderType.addOn.minimumCharge ?? "",
                },
                class: {
                    percentage: serverSnapshot.orderType.class.percentage ?? "",
                    minimumCharge: serverSnapshot.orderType.class.minimumCharge ?? "",
                },
                courts: (serverSnapshot.orderType.courts || []).map((c) => ({
                    courtId: c.courtId,
                    title: c.title,
                    percentage: c.percentage ?? "",
                    minimumCharge: c.minimumCharge ?? "",
                })),
            },
            valuePack: (serverSnapshot.valuePack || []).map((v) => ({
                valuePackCategoryId: v.valuePackCategoryId,
                title: v.title,
                enabled: v.enabled ?? true,
                percentage: v.percentage ?? "",
                minimumCharge: v.minimumCharge ?? "",
            })),
        });
    };

    const validate = () => {
        const checkPercentMin = (title, p, m) => {
            const pct = toNumberOrNull(p);
            const min = toNumberOrNull(m);
            if (pct != null && (pct < 0 || pct > 100)) return `Percentage for "${title}" must be 0-100`;
            if (min != null && min < 0) return `Minimum fee for "${title}" must be >= 0`;
            return null;
        };

        const e1 = checkPercentMin("Court Booking", form.orderType.courtBooking.percentage, form.orderType.courtBooking.minimumCharge);
        if (e1) return e1;

        if (form.orderType.courts) {
            for (const c of form.orderType.courts) {
                const e = checkPercentMin(c.title, c.percentage, c.minimumCharge);
                if (e) return e;
            }
        }

        const e2 = checkPercentMin("Add On", form.orderType.addOn.percentage, form.orderType.addOn.minimumCharge);
        if (e2) return e2;

        const e3 = checkPercentMin("Class", form.orderType.class.percentage, form.orderType.class.minimumCharge);
        if (e3) return e3;

        for (const v of form.valuePack) {
            if (!v.enabled) continue;
            const e = checkPercentMin(v.title, v.percentage, v.minimumCharge);
            if (e) return e;
        }

        return null;
    };

    const buildPayload = () => {
        // Send everything (simple) OR compute diff (optional).
        // Keeping it straightforward: send everything.
        return {
            orderType: {
                courtBooking: {
                    percentage: toNumberOrNull(form.orderType.courtBooking.percentage) ?? 0,
                    minimumCharge: toNumberOrNull(form.orderType.courtBooking.minimumCharge) ?? 0,
                },
                addOn: {
                    percentage: toNumberOrNull(form.orderType.addOn.percentage) ?? 0,
                    minimumCharge: toNumberOrNull(form.orderType.addOn.minimumCharge) ?? 0,
                },
                class: {
                    percentage: toNumberOrNull(form.orderType.class.percentage) ?? 0,
                    minimumCharge: toNumberOrNull(form.orderType.class.minimumCharge) ?? 0,
                },
                // courts: form.orderType.courts.map((c) => ({
                //     courtId: c.courtId,
                //     percentage: toNumberOrNull(c.percentage) ?? 0,
                //     minimumCharge: toNumberOrNull(c.minimumCharge) ?? 0,
                // })),
            },
            valuePack: form.valuePack.map((v) => ({
                valuePackCategoryId: v.valuePackCategoryId,
                enabled: !!v.enabled,
                title: v.title,
                percentage: toNumberOrNull(v.percentage) ?? 0,
                minimumCharge: toNumberOrNull(v.minimumCharge) ?? 0,
            })),
        };
    };

    const onUpdate = async () => {
        const err = validate();
        if (err) {
            toast.error(err); // replace with Snackbar/toast in your app
            return;
        }
        try {
            setIsSaving(true);
            const payload = buildPayload();
            console.log('here payload', payload)
            let { data, error } = await updateMerchantFees(merchantId, {
                ...payload,
                placeId: !merchantId ? placeId : null
            })
            if (error) { toast.error('Please try again') }
            await loadAll();
            toast.success("Updated successfully.");
        } catch (e) {
            console.error(e);
            toast.error("Update failed.");
        } finally {
            setIsSaving(false);
        }
    };

    const loadData = async () => {
        try {
            let { data, error } = await getPackageSportCourtType({
                merchantId: merchantId,
                placeId: !merchantId ? placeId : '653f5d9006520fccd4ab4604'
            });
            if (error) throw error;
            if (data) {
                setRows(data);
            }
        } catch (error) {
        }
    }

    const courtLabelRows = useMemo(() => form.orderType.courts || [], [form.orderType.courts]);
    const valuePackRows = useMemo(() => form.valuePack || [], [form.valuePack]);

    return (
        <Grid item xs={12} md={12} style={{ paddingTop: 0 }}>
            <Card style={{ width: "100%", height: "100%", borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
                <CardContent sx={{ p: 2 }}>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="flex-start">
                        <Box sx={sectionLabelSx}>Order Type :</Box>

                        <Box sx={{ flex: 1 }}>
                            <Stack spacing={1.5}>
                                <FeeRowPercentMin
                                    label="Court Booking"
                                    percentage={form.orderType.courtBooking.percentage}
                                    minimumCharge={form.orderType.courtBooking.minimumCharge}
                                    disabled={isLoading || isSaving}
                                    onChange={(patch) => setOrderPatch("courtBooking", patch)}
                                />

                                <FeeRowPercentMin
                                    label="Add On"
                                    percentage={form.orderType.addOn.percentage}
                                    minimumCharge={form.orderType.addOn.minimumCharge}
                                    disabled={isLoading || isSaving}
                                    onChange={(patch) => setOrderPatch("addOn", patch)}
                                />

                                <FeeRowPercentMin
                                    label="Class"
                                    percentage={form.orderType.class.percentage}
                                    minimumCharge={form.orderType.class.minimumCharge}
                                    disabled={isLoading || isSaving}
                                    onChange={(patch) => setOrderPatch("class", patch)}
                                />
                            </Stack>
                        </Box>
                    </Stack>

                    <Box mt={3}>
                        <Divider />
                    </Box>

                    <Box mt={3}>
                        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="flex-start">
                            <Box sx={sectionLabelSx}>Value Pack :</Box>
                            <Box sx={{ flex: 1 }}>
                                <Stack spacing={1.5}>
                                    {valuePackRows.length === 0 ? (
                                        <Paper sx={{ ...rowPaperSx, pt: 1 }}>
                                            <TCLabel>
                                                {isLoading ? "Loading..." : "No value pack rules."}
                                            </TCLabel>
                                        </Paper>
                                    ) : (
                                        valuePackRows?.map((v, idx) => {
                                            // check v is used
                                            console.log('v', rows, v)
                                            if (!rows.some(vi => vi?._id.toString() == v?.valuePackCategoryId.toString())) { return; }
                                            return (
                                                <FeeRowCheckboxPercentMin
                                                    key={v.valuePackCategoryId || idx}
                                                    label={v.title}
                                                    enabled={v.enabled}
                                                    percentage={v.percentage}
                                                    minimumCharge={v.minimumCharge}
                                                    disabled={isLoading || isSaving}
                                                    onChange={(patch) => setValuePackPatch(idx, patch)}
                                                />
                                            )
                                        })
                                    )}
                                </Stack>
                            </Box>
                        </Stack>
                    </Box>

                    <Box mt={3} sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                        <Button variant="outlined" onClick={onReset} disabled={isLoading || isSaving || !serverSnapshot}>
                            Reset
                        </Button>
                        <Button variant="contained" onClick={onUpdate} disabled={isLoading || isSaving}>
                            {isSaving ? "Updating..." : "Update"}
                        </Button>
                    </Box>

                    <Box mt={4}>
                        <TCTitleLabel>Fee History</TCTitleLabel>
                        <TableComponent
                            columns={[
                                { label: "Modified" },
                                { label: "Admin" },
                                { label: "Title" },
                                { label: "Old Value" },
                                { label: "New Value" },
                            ]}
                            rows={historyRows || []}
                            valueCustomator={(row) => ({
                                updatedAt: <TCLabel>{row.modifiedAt ? moment(row.modifiedAt).format('DD/MM/YYYY HH:mm') : "-"}</TCLabel>,
                                admin: <TCLabel>{row.adminName}</TCLabel>,
                                packageType: <TCLabel>{row.title}</TCLabel>,
                                oldValue: <TCLabel>{row.oldValue}</TCLabel>,
                                newValue: <TCLabel>{row.newValue}</TCLabel>,
                            })}
                            pageChange={(e) => {
                                setFeeFilter({
                                    page: e
                                })
                            }}
                            // loading={isLoadingPackage}
                            totalData={totalDataHistory}
                            page={feeFilter.page}
                        />
                    </Box>
                    {
                        merchantId &&
                        <Box>
                            <TCTitleLabel>Deposit History</TCTitleLabel>
                            <MerchantDeposit
                                rows={merchantDepositList}
                                totalData={totalDataDepositList}
                                loading={false}
                                filter={depositFilter}
                                onChangePage={(e) => {
                                    setDepositFilter({
                                        page: e.page
                                    })
                                }}
                                merchantId={merchantId}
                                refetch={loadMerchantDepositRequest}
                                fromBalance={false}
                            />
                        </Box>
                    }
                </CardContent>
            </Card>
        </Grid>
    );
}